/**
 * Category Tabs with Sticky Behavior
 * Sticks to top when scrolling up, sticks to bottom when scrolling down and hitting bottom
 */

class CategoryTabs {
  constructor(container) {
    this.container = container;
    this.tabs = container.querySelectorAll('.m-category-tabs__tab');
    this.dropdowns = container.querySelectorAll('.m-category-tabs__dropdown');
    this.lastScrollY = window.scrollY;
    this.scrollDirection = 'down';
    this.isSticky = false;
    this.headerHeight = 0;
    this.tabsHeight = 0;
    this.initialTop = 0;
    
    this.init();
  }

  init() {
    if (!this.container) return;
    
    // Calculate initial position
    this.calculatePositions();
    
    // Set up tab click handlers
    this.setupTabHandlers();
    
    // Set up scroll handler
    this.setupScrollHandler();
    
    // Handle resize
    window.addEventListener('resize', () => {
      this.calculatePositions();
    }, { passive: true });
    
    // Handle dropdown clicks outside
    document.addEventListener('click', (e) => {
      if (!this.container.contains(e.target)) {
        this.closeAllDropdowns();
      }
    });
  }

  calculatePositions() {
    // Get header height
    const header = document.querySelector('m-header, header.m-header__desktop, header.m-header__mobile');
    if (header) {
      this.headerHeight = header.offsetHeight;
    }
    
    // Get tabs height
    this.tabsHeight = this.container.offsetHeight;
    
    // Get initial top position
    const rect = this.container.getBoundingClientRect();
    this.initialTop = rect.top + window.scrollY;
    
    // Create spacer element to prevent jump
    this.spacer = document.createElement('div');
    this.spacer.className = 'm-category-tabs__spacer';
    this.spacer.style.height = '0';
    this.spacer.style.transition = 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
    this.spacer.style.visibility = 'hidden';
    this.container.parentNode.insertBefore(this.spacer, this.container.nextSibling);
  }

  setupTabHandlers() {
    // Check current URL to set active tab
    this.updateActiveTabFromURL();
    
    // Listen for URL changes (for browser back/forward)
    window.addEventListener('popstate', () => {
      this.updateActiveTabFromURL();
    });
    
    // For links, let them navigate naturally
    // For buttons (disabled), prevent default
    this.tabs.forEach(tab => {
      if (tab.tagName === 'BUTTON' && tab.disabled) {
        tab.addEventListener('click', (e) => {
          e.preventDefault();
        });
      } else if (tab.tagName === 'A') {
        // Allow navigation, but update active state after navigation
        tab.addEventListener('click', () => {
          // Active state will be updated when page loads
        });
      } else {
        // Button that's not disabled - show dropdown
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          const tabType = tab.getAttribute('data-tab');
          this.switchTab(tabType);
        });
      }
    });
  }
  
  updateActiveTabFromURL() {
    const currentPath = window.location.pathname;
    const isCollectionPage = currentPath.includes('/collections/');
    
    if (!isCollectionPage) {
      // Not on a collection page - remove all active states
      this.tabs.forEach(t => t.classList.remove('active'));
      if (this.dropdowns.length > 0) {
        this.dropdowns.forEach(d => d.classList.remove('active'));
      }
      return;
    }
    
    // Extract collection handle from URL
    const collectionMatch = currentPath.match(/\/collections\/([^\/]+)/);
    if (!collectionMatch) return;
    
    const currentCollectionHandle = collectionMatch[1];
    
    // Get configured collections from data attributes
    const fashionHandles = this.container.getAttribute('data-fashion-handles') || '';
    const accessoriesHandles = this.container.getAttribute('data-accessories-handles') || '';
    
    let activeTab = null;
    
    // Check if current collection is in Fashion collections
    if (fashionHandles) {
      const fashionHandlesArray = fashionHandles.split(',').map(h => h.trim());
      if (fashionHandlesArray.includes(currentCollectionHandle)) {
        activeTab = 'fashion';
      }
    }
    
    // Check if current collection is in Accessories collections
    if (!activeTab && accessoriesHandles) {
      const accessoriesHandlesArray = accessoriesHandles.split(',').map(h => h.trim());
      if (accessoriesHandlesArray.includes(currentCollectionHandle)) {
        activeTab = 'accessories';
      }
    }
    
    // Update active states
    this.tabs.forEach(t => {
      const tabType = t.getAttribute('data-tab');
      if (tabType === activeTab) {
        t.classList.add('active');
      } else {
        t.classList.remove('active');
      }
    });
    
    if (this.dropdowns.length > 0) {
      this.dropdowns.forEach(d => {
        const dropdownType = d.getAttribute('data-dropdown');
        if (dropdownType === activeTab) {
          d.classList.add('active');
        } else {
          d.classList.remove('active');
        }
      });
    }
  }

  switchTab(tabType) {
    // Update active tab
    this.tabs.forEach(t => {
      t.classList.remove('active');
      if (t.getAttribute('data-tab') === tabType) {
        t.classList.add('active');
      }
    });
    
    // Update active dropdown (if it exists)
    if (this.dropdowns.length > 0) {
      this.dropdowns.forEach(dropdown => {
        dropdown.classList.remove('active');
        if (dropdown.getAttribute('data-dropdown') === tabType) {
          dropdown.classList.add('active');
        }
      });
    }
  }

  setupScrollHandler() {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    this.handleScroll();
  }

  handleScroll() {
    const currentScrollY = window.scrollY;
    const scrollDelta = currentScrollY - this.lastScrollY;
    
    // Determine scroll direction (only if there's significant movement)
    if (Math.abs(scrollDelta) > 1) {
      if (scrollDelta > 0) {
        this.scrollDirection = 'down';
      } else if (scrollDelta < 0) {
        this.scrollDirection = 'up';
      }
    }
    
    // Check if we're at the bottom of the page (with some tolerance)
    const scrollHeight = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight,
      document.body.clientHeight,
      document.documentElement.clientHeight
    );
    const isAtBottom = (window.innerHeight + currentScrollY) >= scrollHeight - 20;
    
    // Check if we've scrolled past the initial position
    const hasScrolledPast = currentScrollY > (this.initialTop - this.headerHeight);
    
    // Apply sticky behavior
    if (hasScrolledPast) {
      if (this.scrollDirection === 'up') {
        // Scrolling up - stick to top
        this.stickToTop();
      } else if (this.scrollDirection === 'down' && isAtBottom) {
        // Scrolling down and at bottom - stick to bottom
        this.stickToBottom();
      } else {
        // Scrolling down but not at bottom - stick to top (default)
        this.stickToTop();
      }
    } else {
      // Not scrolled past initial position - remove sticky
      this.removeSticky();
    }
    
    this.lastScrollY = currentScrollY;
  }

  stickToTop() {
    if (!this.container.classList.contains('sticky-top')) {
      // Calculate exact height needed
      const tabsHeight = this.container.offsetHeight;
      
      // Use requestAnimationFrame for smoother transition
      requestAnimationFrame(() => {
        // Set spacer height first to prevent jump
        if (this.spacer) {
          this.spacer.style.height = tabsHeight + 'px';
        }
        
        // Then make tabs sticky
        requestAnimationFrame(() => {
          this.container.classList.remove('sticky-bottom');
          this.container.classList.add('sticky-top');
          
          document.body.classList.add('has-category-tabs-sticky-top');
          document.body.classList.remove('has-category-tabs-sticky-bottom');
          this.isSticky = true;
        });
      });
    }
  }

  stickToBottom() {
    if (!this.container.classList.contains('sticky-bottom')) {
      // Use requestAnimationFrame for smoother transition
      requestAnimationFrame(() => {
        // Remove spacer height when sticking to bottom
        if (this.spacer) {
          this.spacer.style.height = '0';
        }
        
        requestAnimationFrame(() => {
          this.container.classList.remove('sticky-top');
          this.container.classList.add('sticky-bottom');
          
          document.body.classList.add('has-category-tabs-sticky-bottom');
          document.body.classList.remove('has-category-tabs-sticky-top');
          this.isSticky = true;
        });
      });
    }
  }

  removeSticky() {
    if (this.isSticky) {
      // Use requestAnimationFrame for smoother transition
      requestAnimationFrame(() => {
        // Remove spacer height first
        if (this.spacer) {
          this.spacer.style.height = '0';
        }
        
        requestAnimationFrame(() => {
          this.container.classList.remove('sticky-top', 'sticky-bottom');
          document.body.classList.remove('has-category-tabs-sticky-top', 'has-category-tabs-sticky-bottom');
          this.isSticky = false;
        });
      });
    }
  }

  closeAllDropdowns() {
    this.dropdowns.forEach(dropdown => {
      dropdown.classList.remove('active');
    });
    this.tabs.forEach(tab => {
      tab.classList.remove('active');
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCategoryTabs);
} else {
  initCategoryTabs();
}

function initCategoryTabs() {
  const categoryTabsContainers = document.querySelectorAll('.m-category-tabs[data-section-type="category-tabs"]');
  
  categoryTabsContainers.forEach(container => {
    new CategoryTabs(container);
  });
}

// Re-initialize on section load (for theme editor)
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.detail.sectionId;
    const container = document.querySelector(`.m-category-tabs[data-section-id="${section}"]`);
    if (container) {
      new CategoryTabs(container);
    }
  });
}

